import React from 'react';
import { ArrowRight, Play, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';

const LOGO_URL = process.env.REACT_APP_LOGO_URL;

const HeroSection = () => {
  const navigate = useNavigate();

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-black">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,rgba(223,255,0,0.15),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_60%,rgba(223,255,0,0.1),transparent_50%)]" />
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}
        />
      </div>

      {/* Animated LED Dots */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-[#DFFF00] rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 2}s`
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-6 lg:px-8 py-32">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Text Content */}
          <div className="text-center lg:text-left">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-[#DFFF00]/10 border border-[#DFFF00]/30 rounded-full px-4 py-2 mb-8">
              <span className="w-2 h-2 bg-[#DFFF00] rounded-full animate-pulse" />
              <span className="text-[#DFFF00] text-sm font-medium">Nouveau : Location flexible</span>
            </div>

            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              <span className="sm:whitespace-nowrap">Location d'écrans LED</span>
              <span className="block text-[#DFFF00]">
                sans engagement
              </span>
            </h1>

            <p className="text-base sm:text-xl text-slate-300 mb-8 max-w-xl mx-auto lg:mx-0 px-2 sm:px-0">
              Louez vos écrans LED pour la saison, un événement ou une fan zone. 
              <span className="text-white font-medium">1 mois minimum, restitution libre.</span>
            </p>

            {/* Key Points */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-3 sm:gap-4 mb-10 px-2 sm:px-0">
              {['Plug & Play', 'Livraison France', 'Tout inclus'].map((point) => (
                <div key={point} className="flex items-center gap-2 text-slate-300 text-sm sm:text-base">
                  <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-[#DFFF00] flex-shrink-0" />
                  <span>{point}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start px-2 sm:px-0">
              <Button
                onClick={() => navigate('/reservation')}
                className="bg-[#DFFF00] hover:bg-[#c8e600] text-black font-semibold px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg rounded-full shadow-lg shadow-[#DFFF00]/25 hover:shadow-xl hover:shadow-[#DFFF00]/30 transition-all group"
              >
                Réserver maintenant
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                onClick={() => scrollToSection('process')}
                variant="outline"
                className="border-2 border-white/20 text-white hover:bg-white/10 font-semibold px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg rounded-full transition-all group"
              >
                <Play className="mr-2 w-5 h-5" />
                Comment ça marche
              </Button>
            </div>
          </div>

          {/* Visual Element */}
          <div className="relative hidden lg:block">
            <div className="relative flex items-center justify-center">
              {/* Outer Glow - Large diffused */}
              <div className="absolute inset-0 scale-150">
                <div className="absolute inset-0 bg-[#DFFF00]/15 rounded-full blur-[100px] animate-pulse" />
              </div>
              
              {/* Middle Glow - Medium diffused */}
              <div className="absolute inset-0 scale-125">
                <div className="absolute inset-0 bg-[#DFFF00]/20 rounded-full blur-[60px]" />
              </div>
              
              {/* Inner Glow - Closer to image */}
              <div className="absolute -inset-8 bg-gradient-radial from-[#DFFF00]/30 via-[#DFFF00]/10 to-transparent rounded-3xl blur-3xl" />
              
              {/* Main Image */}
              <div className="relative z-10 transform hover:scale-[1.02] transition-transform duration-500">
                <img
                  src="https://customer-assets.emergentagent.com/job_101ab4cd-4d37-431a-b507-ce3ea7ef67e4/artifacts/0bq0c4ll_Pack%20%C3%A9cran%20LED%20Location%207m2%20-%20Phocea%20Rent.png"
                  alt="Pack écran LED Phocea Rent"
                  className="w-auto h-[450px] object-contain drop-shadow-[0_0_30px_rgba(223,255,0,0.3)] mix-blend-lighten"
                  style={{ backgroundColor: 'transparent' }}
                />
              </div>
              
              {/* Price Badge - Top Right */}
              <div className="absolute top-4 right-4 z-20">
                <div className="bg-[#DFFF00] text-black rounded-full px-5 py-3 shadow-lg shadow-[#DFFF00]/50">
                  <p className="text-xs font-medium opacity-80">À partir de</p>
                  <p className="text-2xl font-bold leading-tight">749€<span className="text-sm font-medium">/mois</span></p>
                </div>
              </div>

              {/* Animated floating particles */}
              <div className="absolute top-1/4 left-0 w-3 h-3 bg-[#DFFF00]/40 rounded-full blur-sm animate-float" />
              <div className="absolute bottom-1/3 right-0 w-2 h-2 bg-[#DFFF00]/50 rounded-full blur-sm animate-float-delayed" />
              <div className="absolute top-1/2 left-1/4 w-2 h-2 bg-[#DFFF00]/30 rounded-full blur-sm animate-float" />
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
          <div className="w-1.5 h-3 bg-[#DFFF00]/50 rounded-full animate-pulse" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
